import React from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import styled from "@emotion/styled";

// @ts-ignore
import Abilities from "services/permissions/abilities";
// @ts-ignore
import ContractUtils from "services/contract/utils";
// @ts-ignore
import ContractActioner from "services/contract/actioner";
// @ts-ignore
import UserUtils from "services/user/utils";
// @ts-ignore
import AuthenticationActioner from "services/authentication/actioner";

import paths from "routes/paths";

import HeaderTabs from "components/HeaderTabs";
import AppBar from "components/AppBar";
import LogoutButton from "components/LogoutButton";
import HeaderLogo from "components/HeaderLogo";
import ContractSwitcher from "components/ContractSwitcher";
import { RouteWithChildren } from "types/HeaderTypes";

const LogoContainer = styled.div`
  display: flex;
  max-width: 160px;
  flex: 1;
  align-items: center;
`;

const RightContainer = styled.div`
  display: flex;
`;

const ContentContainer = styled.div`
  overflow-y: scroll;
  flex: 4;
  height: 64px;
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &&::-webkit-scrollbar {
    display: none;
  }
`;

type ContractTypes = {
  name: string;
  id: string;
  disabled: any;
  logo_url: { small: string };
};

type Props = {
  isAuthenticated: boolean;
  currentContract: ContractTypes;
  contracts: ContractTypes[];
  switchContract: Function;
  user: {};
  logout: React.MouseEventHandler<HTMLButtonElement>;
};

function Header({
  isAuthenticated,
  currentContract,
  contracts,
  switchContract,
  user,
  logout,
}: Props): JSX.Element {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const history = useHistory();

  function getTabs(): RouteWithChildren[] {
    return [
      {
        hasAccess: Abilities.ability.can("read", "tabs.live"),
        label: formatMessage({ id: "header.tabs.live" }),
        route: paths.front.tabs.live,
      },
      {
        hasAccess: Abilities.ability.can("read", "tabs.park_stats"),
        label: formatMessage({ id: "header.tabs.park_stats" }),
        route: paths.front.tabs.park_stats,
      },
      {
        hasAccess: Abilities.ability.can("read", "tabs.pedestrian_stats"),
        label: formatMessage({ id: "header.tabs.pedestrian_stats" }),
        route: paths.front.tabs.pedestrian_stats,
      },
      {
        hasAccess: Abilities.ability.can("read", "tabs.nonMovingCars"),
        label: formatMessage({ id: "header.tabs.non_moving_cars" }),
        route: paths.front.tabs.non_moving_cars,
      },
      {
        hasAccess: Abilities.ability.can("read", "tabs.analysis"),
        label: formatMessage({ id: "header.tabs.analysis" }),
        route: paths.front.tabs.analysis,
      },
      {
        hasAccess: Abilities.ability.can("read", "tabs.flow"),
        label: formatMessage({ id: "header.tabs.flow" }),
        route: paths.front.tabs.flow,
      },
      {
        hasAccess: Abilities.ability.can("read", "tabs.customAnalysis"),
        label: formatMessage({ id: "header.tabs.custom_analysis" }),
        route: paths.front.tabs.custom_analysis,
      },
      {
        hasAccess: Abilities.ability.can("read", "tabs.indicators"),
        label: formatMessage({ id: "header.tabs.indicators" }),
        route: paths.front.tabs.indicators,
      },
      {
        hasAccess: Abilities.ability.can("read", "tabs.visitorCharacteristics"),
        label: formatMessage({ id: "header.tabs.visitor_characteristics" }),
        route: paths.front.tabs.visitor_characteristics,
      },
      {
        hasAccess: Abilities.ability.can("read", "tabs.alerts"),
        label: formatMessage({ id: "header.tabs.alerts" }),
        route: paths.front.tabs.alerts,
      },
      {
        hasAccess: Abilities.ability.can("read", "tabs.config"),
        label: formatMessage({ id: "header.tabs.config" }),
        items: [
          {
            hasAccess: Abilities.ability.can("read", "tabs.config_sectors"),
            label: formatMessage({ id: "header.tabs.config_sectors" }),
            route: paths.front.tabs.config_sectors,
          },
          {
            hasAccess: Abilities.ability.can("read", "tabs.config_displays"),
            label: formatMessage({ id: "header.tabs.displays" }),
            route: paths.front.tabs.displays,
          },
          {
            hasAccess: Abilities.ability.can("read", "tabs.config_gates"),
            label: formatMessage({ id: "header.tabs.gates" }),
            route: paths.front.tabs.gates,
          },
        ],
      },
    ].filter((tab) => tab.hasAccess === true);
  }
  function handleTabClicked(clickedTab: { route: string }) {
    // We intentionnally remove search params from URL (to reset live reload param on tab switched)
    history.push({ pathname: clickedTab?.route });
  }

  if (!isAuthenticated) {
    return <></>;
  }

  return (
    <AppBar>
      <>
        <LogoContainer>
          <HeaderLogo
            onClick={() => {
              // @ts-ignore
              clearInterval(window.currentReloadIntervalId);
              history.push({
                pathname: UserUtils.getDefaultRoute(user),
              });
            }}
          />
        </LogoContainer>

        <ContentContainer>
          <HeaderTabs
            onTabClick={handleTabClicked}
            currentUri={location?.pathname}
            tabs={getTabs()}
          />
        </ContentContainer>

        <RightContainer>
          {ContractUtils.shouldHideOnCurrentTab(location?.pathname) ? null : (
            <ContractSwitcher
              switchContract={switchContract}
              currentContract={currentContract}
              contracts={contracts}
            />
          )}
          <LogoutButton onLogout={logout} />
        </RightContainer>
      </>
    </AppBar>
  );
}

const mapStateToProps = (state: any) => ({
  userFullName: UserUtils.getFullName(state.User),
  currentContract: state.Contract.contract,
  contracts: state.Contract.contracts,
  isAuthenticated: state.Authentication.isAuthenticated,
  user: state.User,
});

const mapDispatchToProps = (dispatch: any) => ({
  logout: () => dispatch(AuthenticationActioner.logout()),
  switchContract: (id: string) => dispatch(ContractActioner.switchContract(id)),
});

Header.defaultProps = {
  currentContract: {},
  isAuthenticated: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
